import React from 'react';
import PropTypes from 'prop-types';
import { flatten, takeWhile } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleModel, buildModelFamilyIdsModelYearsPath } from 'client/data/models/vehicle-v2';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';

const RULE_TYPE = { BODY: 'body', FUEL: 'fuel' };

export const selectType = target => {
  const [year, makeSlug, modelSlug, submodelSlug] = target.id.split('|');
  const newLocation = getCoreUrl({ makeSlug, modelSlug, year, submodelSlug });

  window.location.assign(`${window.location.origin}${newLocation}${window.location.search}`);
};

function buildOptionObject({ makeSlug, modelSlug, year, fallbackName, niceId, isDefaultSubmodel, label }) {
  const value = [year, makeSlug, modelSlug, niceId].join('|');
  const id = isDefaultSubmodel ? [year, makeSlug, modelSlug].join('|') : value; // remove submodel for default submodel

  return {
    id,
    value,
    fallbackName,
    label,
  };
}

function handleSubmodels({ submodels, makeSlug, modelSlug, year, modelName }) {
  const useFuelTypeLabels =
    submodels &&
    submodels.some(({ ruleType }) => ruleType === RULE_TYPE.FUEL) &&
    submodels.filter(({ ruleType }) => ruleType === RULE_TYPE.BODY).length === 1;

  return submodels.map(({ niceId, name, ruleType, isDefault }) =>
    buildOptionObject({
      makeSlug,
      modelSlug,
      year,
      niceId,
      isDefaultSubmodel: isDefault,
      label: useFuelTypeLabels && ruleType === RULE_TYPE.BODY ? `${modelName} Gas` : name,
    })
  );
}

function TypeDropdownUI({ modelYears = null, value = null, descriptionId = null }) {
  const modelFamilyModels = modelYears?.filter(({ submodels }) => submodels?.length === 1);
  const submodelModels = modelYears?.filter(({ submodels }) => submodels?.length > 1);

  const submodelOptions = submodelModels?.length ? flatten(submodelModels.map(handleSubmodels)) : [];
  const modelFamilyOptions = modelFamilyModels?.length
    ? modelFamilyModels.map(({ makeSlug, modelSlug, modelName, year, engineType: currentEngineType, submodels }) =>
        buildOptionObject({
          makeSlug,
          modelSlug,
          year,
          fallbackName: submodels[0].name,
          niceId: submodels[0].niceId,
          isDefaultSubmodel: true,
          label:
            currentEngineType &&
            modelFamilyModels.filter(({ engineType }) => engineType === currentEngineType).length === 1 &&
            !modelName.toLowerCase().includes(currentEngineType.toLowerCase())
              ? currentEngineType
              : modelName,
        })
      )
    : [];

  const options = [...submodelOptions, ...modelFamilyOptions];

  // append modelName in front for duplicate option labels
  const deduplicatedOptions = options.map(option => ({
    ...option,
    label: options.filter(({ label }) => option.label === label).length > 1 ? option.fallbackName : option.label,
  }));

  // cut a common prefix of leading words shared by every label
  const words = deduplicatedOptions.map(({ label }) => label.split(' '));
  const commonWordCount = takeWhile(words[0], (word, i) => words.every(w => w[i] === word)).length;
  const trimmedOptions = deduplicatedOptions.map((option, index) => ({
    ...option,
    label: words[index].slice(commonWordCount).join(' ') || option.label,
  }));

  if (deduplicatedOptions.length < 2) {
    return null;
  }

  return (
    <StyledSelect
      value={value}
      onChange={selectType}
      name="select-type"
      labelText="Select a type"
      wrapperClassName="label-inside me-1 mb-1_5 w-100"
      labelClassName="small mb-0_5"
      overrideClasses="type-select d-block w-100 px-1 pt-1_75 pb-0_5 size-16 bg-white text-black rounded-8"
      options={trimmedOptions}
      data-tracking-id="type_select"
      data-tracking-parent="edm-entry-model-summary-submodel"
      aria-describedby={descriptionId}
    />
  );
}

TypeDropdownUI.propTypes = {
  modelYears: PropTypes.arrayOf(
    PropTypes.shape({
      makeSlug: PropTypes.string,
      modelName: PropTypes.string,
      modelSlug: PropTypes.string,
      year: PropTypes.string,
      engineType: PropTypes.string,
      submodels: PropTypes.arrayOf(
        PropTypes.shape({
          niceId: PropTypes.string,
          identifier: PropTypes.string,
          name: PropTypes.string,
          ruleType: PropTypes.string,
          isDefault: PropTypes.bool,
        })
      ),
    })
  ),
  value: PropTypes.string,
  descriptionId: PropTypes.string,
};

export const TypeDropdown = connectToModel(TypeDropdownUI, {
  modelYears: bindToPath(
    ({ modelFamilyIds }) => buildModelFamilyIdsModelYearsPath(modelFamilyIds),
    VehicleModel,
    (modelYears, { params, skipModelFamily }) =>
      modelYears.filter(
        ({ year, modelSlug }) => year === params.year && (modelSlug === params.model || !skipModelFamily)
      )
  ),
});
